import React, { useEffect, useState, useCallback } from 'react';
import { shippingPriceByCapacityApi, shippingPriceApi, vehicleCapacityTonsApi } from '../services/api';
import { ShippingPriceByCapacityHistory, Province, District } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

interface VehicleCapacity {
  id: number;
  capacity: number;
  description?: string;
}

const ShippingPriceByCapacityHistoryPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [history, setHistory] = useState<ShippingPriceByCapacityHistory[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [capacities, setCapacities] = useState<VehicleCapacity[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedProvince, setSelectedProvince] = useState<number | undefined>(undefined);
  const [selectedDistrict, setSelectedDistrict] = useState<number | undefined>(undefined);
  const [selectedCapacity, setSelectedCapacity] = useState<number | undefined>(undefined);

  // Helper function to format currency in Vietnamese format
  const formatVietnamCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN').format(amount) + ' ?';
  };

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await shippingPriceByCapacityApi.getHistory(
      selectedProvince,
        selectedDistrict,
        selectedCapacity
  );
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
    setLoading(false);
    }
  }, [selectedProvince, selectedDistrict, selectedCapacity]);

  const fetchReferenceData = async () => {
    try {
      const [provincesRes, capacitiesRes] = await Promise.all([
        shippingPriceApi.getProvinces(),
        vehicleCapacityTonsApi.getAll()
]);
      setProvinces(provincesRes.data);
      setCapacities(capacitiesRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
}
  };

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
 fetchHistory();
  }, [fetchHistory]);

  const handleProvinceChange = async (provinceId: string) => {
    const id = provinceId ? parseInt(provinceId) : undefined;
    setSelectedProvince(id);
    setSelectedDistrict(undefined);
    
    if (id) {
      try {
const response = await shippingPriceApi.getDistricts(id);
   setDistricts(response.data);
      } catch (error) {
        console.error('Error fetching districts:', error);
        setDistricts([]);
      }
    } else {
      setDistricts([]);
    }
  };

  const getActionBadgeClass = (action: string): string => {
    switch (action.toLowerCase()) {
      case 'created':
        return 'bg-success';
   case 'updated':
        return 'bg-primary';
      case 'deleted':
        return 'bg-danger';
      default:
 return 'bg-secondary';
    }
  };

  return (
    <div className="container-fluid">
   <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <button 
className="btn btn-outline-secondary"
            onClick={() => navigate('/pricing/capacity-matrix')}
    >
         <ArrowLeftOutlined />
       </button>
        <h3 className="mb-0">{t('shipping.price.capacity.history') || 'Shipping Price by Capacity - History'}</h3>
     </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
         <div className="col-md-3">
         <label className="form-label">{t('province') || 'Province'}</label>
  <select 
                className="form-select"
      value={selectedProvince || ''}
 onChange={(e) => handleProvinceChange(e.target.value)}
     >
            <option value="">{t('all.provinces') || 'All Provinces'}</option>
  {provinces.map(p => (
       <option key={p.id} value={p.id}>{p.name}</option>
          ))}
     </select>
            </div>
            <div className="col-md-3">
       <label className="form-label">{t('district') || 'District'}</label>
      <select 
    className="form-select"
     value={selectedDistrict || ''}
      onChange={(e) => setSelectedDistrict(e.target.value ? parseInt(e.target.value) : undefined)}
        disabled={!selectedProvince}
        >
             <option value="">{t('all.districts') || 'All Districts'}</option>
       {districts.map(d => (
   <option key={d.id} value={d.id}>{d.name}</option>
       ))}
      </select>
      </div>
      <div className="col-md-3">
    <label className="form-label">{t('capacity') || 'Capacity'}</label>
       <select 
          className="form-select"
   value={selectedCapacity || ''}
    onChange={(e) => setSelectedCapacity(e.target.value ? parseInt(e.target.value) : undefined)}
              >
             <option value="">{t('all.capacities') || 'All Capacities'}</option>
                {capacities.map(c => (
     <option key={c.id} value={c.id}>{c.capacity} {t('tons') || 'T'}</option>
     ))}
      </select>
</div>
          <div className="col-md-3 d-flex align-items-end">
         <button 
  className="btn btn-outline-primary"
   onClick={fetchHistory}
      >
           {t('refresh') || 'Refresh'}
          </button>
     </div>
     </div>
     </div>
      </div>

      {/* History Table */}
      {loading ? (
        <div className="text-center my-5">
     <div className="spinner-border" role="status">
   <span className="visually-hidden">{t('loading') || 'Loading...'}</span>
          </div>
        </div>
      ) : (
<div className="table-responsive">
          <table className="table table-bordered table-hover">
        <thead className="table-light">
   <tr>
                <th>{t('date') || 'Date'}</th>
     <th>{t('location') || 'Location'}</th>
    <th>{t('type') || 'Type'}</th>
     <th>{t('capacity') || 'Capacity'}</th>
       <th>{t('action') || 'Action'}</th>
       <th className="text-end">{t('old.price') || 'Old Price'}</th>
    <th className="text-end">{t('new.price') || 'New Price'}</th>
  <th>{t('reason') || 'Reason'}</th>
          <th>{t('changed.by') || 'Changed By'}</th>
              </tr>
     </thead>
            <tbody>
     {history.length === 0 ? (
                <tr>
         <td colSpan={9} className="text-center">{t('no.history.found') || 'No history records found'}</td>
       </tr>
       ) : (
    history.map((record) => (
   <tr key={record.id}>
<td>
          {new Date(record.changeDate).toLocaleDateString()}{' '}
<small className="text-muted">{new Date(record.changeDate).toLocaleTimeString()}</small>
        </td>
     <td>{record.locationName}</td>
         <td>
            <span className={`badge ${record.locationType === 'Province' ? 'bg-primary' : 'bg-secondary'}`}>
 {t(record.locationType.toLowerCase()) || record.locationType}
  </span>
        </td>
      <td>{record.capacityTons} {t('tons') || 'T'}</td>
             <td>
       <span className={`badge ${getActionBadgeClass(record.action)}`}>
      {t(record.action.toLowerCase()) || record.action}
            </span>
             </td>
      <td className="text-end">
      {record.oldPrice != null ? formatVietnamCurrency(record.oldPrice) : '-'}
    </td>
         <td className="text-end">
     {formatVietnamCurrency(record.newPrice)}
          </td>
   <td>{record.reason || '-'}</td>
    <td>{record.changedByUserName}</td>
         </tr>
 ))
    )}
   </tbody>
      </table>
   </div>
      )}
    </div>
  );
};

export default ShippingPriceByCapacityHistoryPage;
